import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Link2 } from "lucide-react";
import { toast } from "sonner";
import { adminListPromos, formatINR, apiErrorMessage } from "../../api";
import { PromoFormDialog } from "./PromoFormDialog";
import { PromoOrdersPanel } from "./PromoOrdersPanel";

const discountLabel = (p) => (p.discount_type === "percent" ? `${p.discount_value}% off` : `${formatINR(p.discount_value)} off`);
export const promoLink = (code) => `${window.location.origin}/?promo=${encodeURIComponent(code)}`;
const copyLink = async (code) => {
  try {
    await navigator.clipboard.writeText(promoLink(code));
    toast.success("Link copied", { description: promoLink(code) });
  } catch (e) {
    window.prompt("Copy this link:", promoLink(code));
  }
};
const dateInput = "h-10 px-3 border border-[var(--sand)] bg-white text-sm focus:outline-none focus:border-[var(--copper)]";

const AdminPromos = () => {
  const [promos, setPromos] = useState(null);
  const [range, setRange] = useState({ start: "", end: "" });
  const [editing, setEditing] = useState(undefined);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(() => {
    adminListPromos({ start: range.start || undefined, end: range.end || undefined })
      .then(setPromos).catch((e) => toast.error(apiErrorMessage(e)));
  }, [range.start, range.end]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-8 lg:p-12" data-testid="admin-promos-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif-display text-4xl text-[var(--espresso)]">Promo Codes</h1>
          <p className="text-sm text-[var(--espresso)]/60 mt-2">Partner codes, usage and commission.</p>
        </div>
        <button onClick={() => setEditing(null)} data-testid="promo-new-btn" className="h-11 px-6 vc-btn-copper text-xs tracking-[0.2em] uppercase flex items-center gap-2"><Plus className="w-4 h-4" /> New code</button>
      </div>

      <div className="flex flex-wrap items-end gap-3 mt-8">
        <label className="text-[10px] tracking-[0.2em] uppercase text-[var(--espresso)]/60">From<input type="date" value={range.start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} data-testid="promo-filter-start" className={`${dateInput} block mt-1`} /></label>
        <label className="text-[10px] tracking-[0.2em] uppercase text-[var(--espresso)]/60">To<input type="date" value={range.end} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} data-testid="promo-filter-end" className={`${dateInput} block mt-1`} /></label>
        {(range.start || range.end) && <button onClick={() => setRange({ start: "", end: "" })} data-testid="promo-filter-clear" className="h-10 px-4 text-xs tracking-[0.14em] uppercase hover:text-[var(--copper)]">Clear</button>}
      </div>

      {!promos ? (
        <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[var(--copper)]" /></div>
      ) : promos.length === 0 ? (
        <div className="bg-white p-12 text-center border border-[var(--sand)] mt-6"><p className="font-serif-display text-2xl">No promo codes yet.</p></div>
      ) : (
        <div className="mt-6 bg-white border border-[var(--sand)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] tracking-[0.14em] uppercase text-[var(--espresso)]/60 border-b border-[var(--sand)]">
                {["Code", "Discount", "Valid", "Used", "Sales", "Discount given", "Commission", "Paid", "Pending", ""].map((h) => <th key={h} className="text-left px-4 py-3 whitespace-nowrap">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {promos.map((p) => (
                <tr key={p.id} onClick={() => setViewing(p)} data-testid={`promo-row-${p.code}`} className="border-b border-[var(--sand)] last:border-0 hover:bg-[var(--ivory)]/50 cursor-pointer">
                  <td className="px-4 py-4">
                    <p className="font-serif-display text-lg flex items-center gap-2">{p.code}{!p.active && <span className="text-[9px] tracking-widest uppercase bg-[var(--sand)] px-1.5 py-0.5 font-sans">Inactive</span>}</p>
                    <p className="text-xs text-[var(--espresso)]/55">{p.partner_name} · {p.commission_percent}% commission</p>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">{discountLabel(p)}{p.min_order ? <p className="text-[11px] text-[var(--espresso)]/50">Min {formatINR(p.min_order)}</p> : null}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-xs">{p.starts_at} → {p.expires_at}</td>
                  <td className="px-4 py-4 whitespace-nowrap">{p.stats.uses}{p.max_uses ? ` / ${p.max_uses}` : ""}</td>
                  <td className="px-4 py-4 whitespace-nowrap">{formatINR(p.stats.sales)}</td>
                  <td className="px-4 py-4 whitespace-nowrap">{formatINR(p.stats.discount_given)}</td>
                  <td className="px-4 py-4 whitespace-nowrap">{formatINR(p.stats.commission_earned)}</td>
                  <td className="px-4 py-4 whitespace-nowrap">{formatINR(p.stats.commission_paid)}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-[var(--copper)]">{formatINR(p.stats.commission_pending)}</td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <button onClick={(e) => { e.stopPropagation(); copyLink(p.code); }} data-testid={`promo-copy-link-${p.code}`} aria-label={`Copy link for ${p.code}`} className="p-2 hover:text-[var(--copper)]"><Link2 className="w-4 h-4" /></button>
                    <button onClick={(e) => { e.stopPropagation(); setEditing(p); }} data-testid={`promo-edit-${p.code}`} aria-label={`Edit ${p.code}`} className="p-2 hover:text-[var(--copper)]"><Pencil className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PromoFormDialog open={editing !== undefined} promo={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />
      {viewing && <PromoOrdersPanel promo={viewing} range={range} onClose={() => setViewing(null)} onChanged={load} />}
    </div>
  );
};

export default AdminPromos;
